import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

export type AuthView = 'login' | 'signup';

/** Login / signup popup: which form is open, where to go after logging in, referral code. */
export interface AuthModalState {
  view: AuthView | null;
  returnTo?: string;
  referralCode?: string;
}

interface UIState {
  isLoading: boolean;
  error: string | null;
  notifications: Notification[];
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  rulesModalOpen: boolean;
  ageModalOpen: boolean;
  editStakeOpen: boolean;
  /** The header's Deposit popup (the /deposit page stays as it is). */
  depositModalOpen: boolean;
  /** The header's Withdraw popup (the /withdraw page stays as it is). */
  withdrawModalOpen: boolean;
  authModal: AuthModalState;
}

const initialState: UIState = {
  isLoading: false,
  error: null,
  notifications: [],
  sidebarOpen: true,
  sidebarCollapsed: false,
  rulesModalOpen: false,
  ageModalOpen: false,
  editStakeOpen: false,
  depositModalOpen: false,
  withdrawModalOpen: false,
  authModal: { view: null },
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    setNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== action.payload
      );
    },
    clearNotification: (state) => {
      state.notifications = [];
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },
    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setRulesModalOpen: (state, action: PayloadAction<boolean>) => {
      state.rulesModalOpen = action.payload;
    },
    toggleRulesModalOpen: (state) => {
      state.rulesModalOpen = !state.rulesModalOpen;
    },
    setAgeModalOpen: (state, action: PayloadAction<boolean>) => {
      state.ageModalOpen = action.payload;
    },
    toggleAgeModalOpen: (state) => {
      state.ageModalOpen = !state.ageModalOpen;
    },
    setEditStake: (state, action: PayloadAction<boolean>) => {
      state.editStakeOpen = action.payload;
    },
    toggleEditStake: (state) => {
      state.editStakeOpen = !state.editStakeOpen;
    },
    setDepositModalOpen: (state, action: PayloadAction<boolean>) => {
      state.depositModalOpen = action.payload;
    },
    setWithdrawModalOpen: (state, action: PayloadAction<boolean>) => {
      state.withdrawModalOpen = action.payload;
    },
    /** Open the login or signup popup (a bare view name, or with returnTo / referralCode). */
    openAuthModal: (
      state,
      action: PayloadAction<AuthView | { view: AuthView; returnTo?: string; referralCode?: string }>
    ) => {
      state.authModal =
        typeof action.payload === 'string' ? { view: action.payload } : action.payload;
    },
    /** Switch between login and signup without losing returnTo / referralCode. */
    setAuthView: (state, action: PayloadAction<AuthView>) => {
      state.authModal.view = action.payload;
    },
    closeAuthModal: (state) => {
      state.authModal = { view: null };
    },
  },
});

export const {
  setLoading,
  setError,
  clearError,
  setNotification,
  removeNotification,
  clearNotification,
  setSidebarOpen,
  toggleSidebar,
  setSidebarCollapsed,
  toggleSidebarCollapsed,
  setRulesModalOpen,
  toggleRulesModalOpen,
  setAgeModalOpen,
  toggleAgeModalOpen,
  setEditStake,
  toggleEditStake,
  openAuthModal,
  setAuthView,
  closeAuthModal,
  setDepositModalOpen,
  setWithdrawModalOpen,
} = uiSlice.actions;
